import { useEffect, useState } from "react";
import { Rotate3d } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import TourExplorer, { TourModal } from "@/components/TourExplorer";
import CTASection from "@/components/CTASection";
import { COMPANY } from "@/data/site";
import { useTours } from "@/data/useTours";

const APPLICATIONS = ["Hotels", "Restaurants", "Real Estate", "Offices", "Educational Institutions", "Retail Spaces", "Clinics", "Showrooms"];

const Experiences360 = () => {
  const [preview, setPreview] = useState(false);
  const tours = useTours();
  useEffect(() => {
    document.title = "360° Experiences — Click World Online";
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="360° Experiences"
        title={<>Let the world <span className="text-electric">step inside.</span></>}
        text={`Immersive 360° photography and Google Street View virtual tours — ${tours.length ? tours.length : "400"}+ published experiences captured and published by Click World Online.`}
      />

      <section className="bg-navy-deep" data-testid="applications-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="Where 360° Works"
                title={<>Any space that <span className="text-electric">sells itself.</span></>}
                text="If customers choose you partly because of your space, a virtual tour lets them experience it before they visit — building trust before the first hello."
              />
              <div className="mt-10 flex flex-wrap gap-3">
                {APPLICATIONS.map((a, i) => (
                  <Reveal key={a} delay={0.04 * i}>
                    <span data-testid={`application-${a.toLowerCase().replace(/\s/g, "-")}`} className="border border-white/15 text-mist/80 rounded-full px-5 py-2.5 text-sm font-medium">
                      {a}
                    </span>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <button
                  onClick={() => setPreview(true)}
                  data-testid="featured-tour-preview"
                  className="group relative w-full text-left bg-navy-light border border-white/10 rounded-3xl p-8 overflow-hidden hover:border-electric/40 transition-colors duration-300 grain"
                >
                  <div className="absolute inset-0 dot-grid opacity-20" aria-hidden="true" />
                  <div className="relative">
                    <span className="w-14 h-14 rounded-full bg-brand/20 border border-electric/30 flex items-center justify-center text-electric">
                      <Rotate3d size={24} />
                    </span>
                    <h3 className="mt-6 font-display text-2xl font-extrabold text-mist tracking-tight">Try a live 360° view</h3>
                    <p className="mt-3 text-slatec text-sm leading-relaxed">
                      Open an interactive preview and drag to look around — exactly how your customers would experience
                      your space.
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-electric text-sm font-semibold group-hover:gap-3 transition-[gap] duration-300">
                      Open preview →
                    </span>
                  </div>
                </button>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-navy border-t border-white/5" data-testid="tour-portfolio-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionHeading
            eyebrow="Published Work"
            title={<>The 360° portfolio.</>}
            text="Genuine tours captured and published by Click World Online. Click any experience to explore it live."
          />
          <div className="mt-14">
            <TourExplorer />
          </div>
        </div>
      </section>

      <AnimatePresence>
        {preview && <TourModal title="Live 360° Preview" embedUrl={COMPANY.featuredTourEmbed} mapsUrl={null} onClose={() => setPreview(false)} />}
      </AnimatePresence>

      <CTASection title="Ready to put your space on the map?" text="Let's capture your business in 360° and publish it where your customers are already looking." />
    </>
  );
};

export default Experiences360;
